import { useState } from "react";
import { docsClient } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

interface BulkTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  onTagsUpdated: () => void;
}

export default function BulkTagDialog({
  open,
  onOpenChange,
  selectedIds,
  onTagsUpdated,
}: BulkTagDialogProps) {
  const [loading, setLoading] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const { toast } = useToast();

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !tags.includes(tag)) {
      if (tags.length >= 10) {
        toast({
          title: "Error",
          description: "Maximum 10 tags allowed",
          variant: "destructive",
        });
        return;
      }
      if (tag.length > 20) {
        toast({
          title: "Error",
          description: "Tags must be 20 characters or less",
          variant: "destructive",
        });
        return;
      }
      setTags([...tags, tag]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // @ts-expect-error - Supabase generated types issue
      const { error } = await docsClient.rpc("batch_update_tags", {
        p_document_ids: selectedIds,
        p_tags: tags,
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: `Updated tags for ${selectedIds.length} document(s)`,
      });

      setTags([]);
      setTagInput("");
      onOpenChange(false);
      onTagsUpdated();
    } catch (error) {
      console.error("Error updating tags:", error);
      toast({
        title: "Error",
        description: "Failed to update tags",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Update Tags for {selectedIds.length} Documents</DialogTitle>
            <DialogDescription>
              This will replace all existing tags on the selected documents with the tags you
              specify below.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="bulk-tags">Tags - Press Enter to add</Label>
              <div className="flex gap-2">
                <Input
                  id="bulk-tags"
                  placeholder="e.g., api, authentication"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                />
                <Button type="button" variant="outline" onClick={handleAddTag} disabled={loading}>
                  Add
                </Button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="gap-1">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Max 10 tags, 20 characters each. This will replace all existing tags on selected
                documents.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || tags.length === 0}>
              {loading ? "Updating..." : "Update Tags"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
